<template lang="pug">
  .pagination-container
    transition-group(
      v-if="_items.length"
      :class="listClass"
      :tag="listTag"
      name="staggered-fade"
      :css="false"
      mode="out-in"
    )
      template(v-for="(item, index) in _items")
        slot(:item="item" :index="index")
    nav(
      v-show="_total > 1"
      :class="navClass"
      role="navigation"
      aria-label="pagination"
    )
      button.pagination-prev(
        type="button"
        @click="prevPage"
        @keyup.enter="prevPage"
        :class="[prevLinkClass, { 'disabled': firstPageSelected }]"
        :disabled="firstPageSelected"
        tabindex="0"
      )
        slot(name="prev")
          | {{ prevText }}
      button.pagination-next(
        type="button"
        @click="nextPage"
        @keyup.enter="nextPage"
        :class="[nextLinkClass, { 'disabled': lastPageSelected }]"
        :disabled="lastPageSelected"
        tabindex="0"
      )
        slot(name="next")
          | {{ nextText }}
      ul(:class="containerClass")
        li(
          v-for="page in pages"
          :class="[pageClass, { active: page.selected, disabled: page.disabled }]"
        )
          span.pagination-ellipsis(v-if="page.disabled" tabindex="0")
            | {{ page.content }}
          button.pagination-link(
            v-else=""
            type="button"
            :disabled="page.disabled"
            @click="handlePageSelected(page.index)"
            @keyup.enter="handlePageSelected(page.index)"
            :class="[pageLinkClass, { 'is-active': page.selected }]"
            tabindex="0"
          )
            | {{ page.content }}
</template>

<script>
export default {
    props: {
        items: {
            type: Array,
            required: false
        },
        lastPage: {
            type: Number,
            required: false
        },
        perPage: {
            type: Number,
            default: 10
        },
        currentPage: {
            type: Number,
            default: 1
        },
        // forcePage: {
        //   type: Number
        // },
        step: {
            type: Number,
            default: 3
        },
        marginPages: {
            type: Number,
            default: 1
        },
        prevText: {
            type: String,
            default: 'Prev'
        },
        nextText: {
            type: String,
            default: 'Next'
        },
        navClass: {
            type: String,
            default: 'pagination is-small'
        },
        containerClass: {
            type: String,
            default: 'pagination-list'
        },
        pageClass: {
            type: String
        },
        listTag: {
            type: String,
            default: 'ul'
        },
        transitionGroupClass: {
            type: String,
            default: 'collection'
        },
        pageLinkClass: {
            type: String,
            default: 'button is-outlined'
        },
        prevLinkClass: {
            type: String,
            default: 'button is-outlined'
        },
        nextLinkClass: {
            type: String,
            default: 'button is-outlined'
        }
    },
    data() {
        return {
            selected: this.currentPage - 1
        };
    },
    // watch: {
    //   forcePage (to, from) {
    //     if (to !== this.selected) this.selected = to
    //   }
    // },
    computed: {
        _total() {
            let total = this.lastPage;
            if (this.items && this.items.length) total = Math.ceil(this.items.length / this.perPage);

            return total;
        },
        _items() {
            let items = [];
            if (this.items && this.items.length) {
                // if (this.selected * this.perPage <= this.items.length) {
                // }

                const index = this.selected * this.perPage;
                items = this.items.slice(index, index + this.perPage);
            }
            return items;
        },
        pages() {
            let items = {};
            if (this._total <= this.step) {
                for (let index = 0; index < this._total; index++) {
                    let page = {
                        index: index,
                        content: index + 1,
                        selected: index === this.selected
                    };
                    items[index] = page;
                }
            } else {
                let leftPart = this.step / 2;
                let rightPart = this.step - leftPart;

                if (this.selected < leftPart) {
                    leftPart = this.selected;
                    rightPart = this.step - leftPart;
                } else if (this.selected > this._total - this.step / 2) {
                    rightPart = this._total - this.selected;
                    leftPart = this.step - rightPart;
                }

                // items logic extracted into function
                const mapItems = index => {
                    let page = {
                        index: index,
                        content: index + 1,
                        selected: index === this.selected
                    };

                    if (index <= this.marginPages - 1 || index >= this._total - this.marginPages) {
                        items[index] = page;
                        return;
                    }

                    let breakView = {
                        content: '...',
                        disabled: true
                    };

                    if (this.selected - leftPart > this.marginPages && items[this.marginPages] !== breakView) {
                        items[this.marginPages] = breakView;
                    }

                    if (
                        this.selected + rightPart < this._total - this.marginPages - 1 &&
                        items[this._total - this.marginPages - 1] !== breakView
                    ) {
                        items[this._total - this.marginPages - 1] = breakView;
                    }

                    let overCount = this.selected + rightPart - this._total + 1;

                    if (overCount > 0 && index === this.selected - leftPart - overCount) {
                        items[index] = page;
                    }

                    if (index >= this.selected - leftPart && index <= this.selected + rightPart) {
                        items[index] = page;
                        return;
                    }
                };

                // 1st - loop through low end of margin pages
                for (let i = 0; i < this.marginPages; i++) {
                    mapItems(i);
                }

                // 2nd - loop through high end of margin pages
                for (let i = this._total - 1; i >= this._total - this.marginPages; i--) {
                    mapItems(i);
                }

                // 3rd - loop through selected range
                let selectedRangeLow = 0;
                if (this.selected - this.step > 0) {
                    selectedRangeLow = this.selected - this.step + 1;
                }

                let selectedRangeHigh = this._total;
                if (this.selected + this.step < this._total) {
                    selectedRangeHigh = this.selected + this.step;
                }

                for (let i = selectedRangeLow; i < selectedRangeHigh; i++) {
                    mapItems(i);
                }
            }
            return items;
        },
        firstPageSelected() {
            return this.selected === 0;
        },
        lastPageSelected() {
            return this.selected === this._total - 1 || this._total === 0;
        }
    },
    methods: {
        handlePageSelected(selected) {
            if (this.selected === selected) return;

            this.selected = selected;

            let payload = this.selected + 1;
            if (this._items.length) payload = { page: this.selected + 1, items: this._items };
            this.$emit('change', payload);
        },
        prevPage() {
            if (this.selected <= 0) return;

            this.selected--;

            let payload = this.selected + 1;
            if (this._items.length) payload = { page: this.selected + 1, items: this._items };
            this.$emit('change', payload);
        },
        nextPage() {
            if (this.selected >= this._total - 1) return;

            this.selected++;

            let payload = this.selected + 1;
            if (this._items.length) payload = { page: this.selected + 1, items: this._items };
            this.$emit('change', payload);
        }
    },
    mounted() {
        // let payload = this.selected + 1
        // if (this._items.length) payload = { page: this.selected + 1, items: this._items }
        // this.$emit('change', payload)
    }
};
</script>

<style lang="sass" scoped>
.pagination
  @apply flex justify-between items-center text-center
  width: 100%
  .pagination-list
    @apply flex items-center text-center flex-grow flex-shrink justify-start
    order: 1
  .pagination-previous,
  .pagination-next
    @apply justify-center text-center
  .pagination-previous
    order: 2
  .pagination-next
    order: 3
  .pagination-ellipsis
    width: 2em
    display: inline-block
  &.is-centered
    .pagination-list
      @apply justify-center
    .pagination-list
      order: 2
    .pagination-previous
      order: 1
    .pagination-next
      order: 3
  &.is-right
    .pagination-list
      @apply justify-end
    .pagination-list
      order: 3
    .pagination-previous
      order: 1
    .pagination-next
      order: 2
  &.is-rounded
    button
      border-radius: 2rem
  &.is-small
    font-size: .8rem
    .button
      height: 2rem
      min-width: 2rem

button
  outline: none
  cursor: pointer
  font-size: 1em
  justify-content: center
  margin: .25rem
  padding-left: .5em
  padding-right: .5em
  text-align: center
  line-height: 1
  min-width: 2.5em
</style>
